import type {
  InvalidSgmOpportunity,
  MultipleAvailableOpportunity,
  MultipleMinOddsRestrictedOpportunity,
  MultiplePlayMoneyRestrictedOpportunity,
  SingleAvailableOpportunity,
  SingleMinOddsRestrictedOpportunity,
  SinglePlayMoneyRestrictedOpportunity,
  SingleUnavailableOpportunity
} from '@ps/isp-web-services-types';
import { BetType } from '@ps/isp-web-services-types';

import type { PlaceBetResp, PlaceBetResult, PlaceBetResultLeg } from '@swf/sports/types/place-bet';
import { buildOpportunityId } from '@swf/sports/utils/buildOpportunityId';
import { getBetNo } from '@swf/sports/utils/getBetNo';

import { rehydratedInitialState } from '..';
import type {
  BetReceipt,
  BetSlipState,
  MultipleReceiptItem,
  MultipleRunner,
  OpportunitiesInput,
  SingleReceiptItem
} from '../types';
import { getIsAvailableOpportunity, getIsMultipleOpportunity } from '../utils/getIsOpportunity';

const getMultipleDetails = (legs: PlaceBetResultLeg[], state: BetSlipState): MultipleRunner[] => {
  const allLegsRunners = legs.flatMap(({ leg, winOdds }) =>
    leg.betRunners.map(({ runner }) => ({
      runner,
      winOdds
    }))
  );

  return allLegsRunners.map(({ runner, winOdds }) => {
    const {
      description,
      name,
      handicap,
      placeTerms,
      isStartingPriceOpportunity,
      eventTypeId,
      marketType,
      eventInPlay,
      isStarSubEnabled
    } = state.opportunities.SINGLE.find(
      ({ id }) => buildOpportunityId(BetType.SINGLE, [runner]) === id
    ) as SingleAvailableOpportunity;

    return {
      ...runner,
      eventTypeId,
      description,
      name,
      handicap,
      winRunnerOdds: winOdds,
      placeTerms,
      isStartingPriceOpportunity,
      marketType,
      eventInPlay,
      isStarSubEnabled
    };
  });
};

type GenerateReceiptItem<T> = T extends SingleAvailableOpportunity
  ? SingleReceiptItem
  : MultipleReceiptItem;

const generateReceiptItem =
  (
    data: PlaceBetResult[],
    state: BetSlipState,
    formValues: OpportunitiesInput,
    singleOpportunitiesCount?: number
  ) =>
  <T extends SingleAvailableOpportunity | MultipleAvailableOpportunity>(
    opportunity: T,
    opportunityIndex: number
  ): GenerateReceiptItem<T> => {
    const opportunityBetNo = getBetNo(
      opportunityIndex,
      getIsMultipleOpportunity(opportunity) ? (singleOpportunitiesCount ?? 0) : 0
    );
    const {
      betId,
      betReceiptId,
      totalStake,
      totalPotentialWin,
      betPrice,
      legs,
      wallets,
      regulatorId
    } = data.find((result) => result.betNo === opportunityBetNo) || {};

    const { eachWaySelected } = formValues[opportunity.id] || {};
    const bonusCashWallet = wallets?.find((wallet) => wallet.type === 'BONUS_CASH');

    return {
      ...opportunity,
      betId,
      betReceiptId,
      totalStake: (totalStake || 0) * 100,
      totalPotentialWin: (totalPotentialWin || 0) * 100,
      legs,
      ...(getIsMultipleOpportunity(opportunity)
        ? { betPrice, runners: getMultipleDetails(legs ?? [], state) }
        : {}),
      eachWaySelected,
      isFreeBet: bonusCashWallet ? !!bonusCashWallet.nonRedeemableAmount : false,
      appliedBonusCash: bonusCashWallet ? (bonusCashWallet.amount || 0) * 100 : 0,
      regulatorId
    } as GenerateReceiptItem<T>;
  };

const removePromotionsFromOpportunities = (
  opportunities: BetSlipState['opportunities']
): {
  SINGLE: (
    | Omit<SingleAvailableOpportunity, 'applicableWallets' | 'hasBonusMoney'>
    | SingleMinOddsRestrictedOpportunity
    | SinglePlayMoneyRestrictedOpportunity
    | SingleUnavailableOpportunity
  )[];
  MULTIPLE: (
    | Omit<MultipleAvailableOpportunity, 'applicableWallets' | 'hasBonusMoney'>
    | MultipleMinOddsRestrictedOpportunity
    | MultiplePlayMoneyRestrictedOpportunity
    | InvalidSgmOpportunity
  )[];
} => ({
  SINGLE: opportunities.SINGLE.map((opportunity) => {
    if (!getIsAvailableOpportunity(opportunity)) return opportunity;
    const { applicableWallets, hasBonusMoney, ...restOpportunity } = opportunity;
    return restOpportunity;
  }),
  MULTIPLE: opportunities.MULTIPLE.map((opportunity) => {
    if (!getIsAvailableOpportunity(opportunity)) return opportunity;
    const { applicableWallets, hasBonusMoney, ...restOpportunity } = opportunity;
    return restOpportunity;
  })
});

const betPlacedSuccessfully = (
  state: BetSlipState,
  data: {
    opportunities: BetSlipState['opportunities'];
    formValues: OpportunitiesInput;
    response: PlaceBetResp;
  }
): BetSlipState => {
  const {
    response: { result },
    formValues,
    opportunities
  } = data;

  if (!result) {
    return state;
  }

  const singleAvailableOpportunity = opportunities.SINGLE as SingleAvailableOpportunity[];
  const multipleAvailableOpportunity = opportunities.MULTIPLE.filter((opportunity) =>
    getIsAvailableOpportunity(opportunity)
  ) as MultipleAvailableOpportunity[];

  const receipt: BetReceipt = {
    SINGLE: singleAvailableOpportunity.map(generateReceiptItem(result, state, formValues)),
    MULTIPLE: multipleAvailableOpportunity.map(
      generateReceiptItem(result, state, formValues, singleAvailableOpportunity.length)
    )
  };

  return {
    ...rehydratedInitialState,
    receipt,
    submittedOpportunities: removePromotionsFromOpportunities(state.opportunities)
  };
};

export default betPlacedSuccessfully;
